import {EyeIcon} from "@/components/shared";
import {useNeoContext} from "@/context/neo/neo.context";
import {CustomerType} from "@/libs/enums/live";
import dynamic from "next/dynamic";
import React from "react";
import Loader from "../Loader";
const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});
interface TotalAttendanceProps {
  isLoading: boolean;
  totalAttendance: any;
  totalAttChartOption: any;
  totalAttChartKey: number;
  totalAttSeries: any;
  openPopup: (title: popupParams) => void;
}
const buildChartOptions = (chartOption: any, hasData: boolean) =>
  hasData
    ? chartOption
    : {
        ...chartOption,
        labels: ["No Data"],
        colors: ["#E5E7EB"],
        plotOptions: {
          ...chartOption.plotOptions,
          pie: {
            ...chartOption.plotOptions?.pie,
            donut: {
              ...chartOption.plotOptions?.pie?.donut,
              labels: {
                ...chartOption.plotOptions?.pie?.donut?.labels,
                total: {
                  ...chartOption.plotOptions?.pie?.donut?.labels?.total,
                  formatter: () => "0"
                },
                value: {
                  ...chartOption.plotOptions?.pie?.donut?.labels?.value,
                  formatter: () => "0"
                }
              }
            }
          }
        },
        tooltip: {
          enabled: false
        }
      };

const AttendanceLegendHorizontal: React.FC<{
  present: number;
  absent: number;
}> = ({present, absent}) => (
  <div className="flex items-center justify-center gap-6 pt-2">
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100">
        <svg
          className="h-4 w-4 text-blue-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-7.364 7.364a1 1 0 01-1.414 0L3.293 9.293a1 1 0 011.414-1.414L8 11.172l6.293-6.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div>
        <h5 className="text-xs text-slate-500">Present</h5>
        <h5 className="text-base font-semibold">{present}</h5>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-orange-100">
        <svg
          className="h-4 w-4 text-orange-600"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </div>
      <div>
        <h5 className="text-xs text-slate-500">Absent</h5>
        <h5 className="text-base font-semibold">{absent}</h5>
      </div>
    </div>
  </div>
);

const TotalAttendanceWidget: React.FC<TotalAttendanceProps> = ({
  isLoading,
  totalAttendance,
  totalAttChartKey,
  totalAttChartOption,
  totalAttSeries,
  openPopup
}) => {
  const {filterContext} = useNeoContext();
  const isEmployeeTracking =
    filterContext?.client?.customerType === CustomerType.EMPLOYEETRACKING;

  const hasAttendanceData = totalAttSeries.some(
    (value: number) => Number(value) > 0
  );
  const attendanceChartSeries = hasAttendanceData ? totalAttSeries : [1];
  const attendanceChartOptions = buildChartOptions(
    totalAttChartOption,
    hasAttendanceData
  );

  const staffSeries = [
    totalAttendance.staffPresent ?? 0,
    totalAttendance.staffAbsent ?? 0
  ];
  const hasStaffData = staffSeries.some(value => Number(value) > 0);
  const staffChartSeries = hasStaffData ? staffSeries : [1];
  const staffChartOptions = buildChartOptions(
    totalAttChartOption,
    hasStaffData
  );

  const chartSize = totalAttendance.opted ? "140" : "180";

  return (
    <>
      <div className="rounded-2xl border bg-neo-white ">
        <div className="p-2">
          <div className="flex items-center justify-between px-2">
            <h5 className="text-lg font-semibold">Total Attendance</h5>
            <h5
              className="cursor-pointer"
              title="View"
              onClick={() => openPopup({title: "Total Attendance"})}
            >
              <EyeIcon size={18} />
            </h5>
          </div>
          <div className="flex items-center justify-between px-2">
            {/* <p className="text-sm text-gray-500">
              Attendance summary of students in the fleet.
            </p> */}
          </div>
          <div
            className={`mt-5 rounded-full border-[#FFC7271A] bg-[#FFC7271A] ${isLoading ? "hidden" : "block"}`}
          >
            {/* <div className="justify-left flex">
              <img
                className="h-12 w-12 max-w-xs p-2"
                src="/icons/UserGroup.svg"
                alt="Trip"
              />
              <h5 className="pt-2 text-base font-semibold">
                Total Students{" "}
                <span className="ml-3 text-xl font-semibold">
                  {totalAttendance.total}
                </span>
              </h5>
            </div> */}
          </div>
          {isLoading ? (
            <Loader />
          ) : totalAttendance.opted ? (
            <div className="grid grid-cols-1 gap-2 p-1 sm:grid-cols-2">
              <div className="flex flex-col items-center">
                <h5 className="w-full px-2 text-xs font-semibold uppercase tracking-wide text-blue-500">
                    {isEmployeeTracking ? "Employees" : "Students"}
                </h5>
                <Chart
                  key={totalAttChartKey}
                  options={attendanceChartOptions}
                  series={attendanceChartSeries}
                  type="donut"
                  width={chartSize}
                  height={chartSize}
                />
                <AttendanceLegendHorizontal
                  present={totalAttendance.present}
                  absent={totalAttendance.absent}
                />
              </div>
              <div className="flex flex-col items-center border-slate-200 sm:border-l sm:border-dashed">
                <h5 className="w-full px-2 text-xs font-semibold uppercase tracking-wide text-yellow-500">
                  Staff
                </h5>
                <Chart
                  key={`staff-${totalAttendance.staffPresent}-${totalAttendance.staffAbsent}`}
                  options={staffChartOptions}
                  series={staffChartSeries}
                  type="donut"
                  width={chartSize}
                  height={chartSize}
                />
                <AttendanceLegendHorizontal
                  present={totalAttendance.staffPresent}
                  absent={totalAttendance.staffAbsent}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 p-2">
              <Chart
                key={totalAttChartKey}
                options={attendanceChartOptions}
                series={attendanceChartSeries}
                type="donut"
                width={chartSize}
                height={chartSize}
              />
              <AttendanceLegendHorizontal
                present={totalAttendance.present}
                absent={totalAttendance.absent}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TotalAttendanceWidget;
